<template>
  <div class="container-fluid p-4">
    <h4 class="text-primary mb-4">
      <i class="bi bi-globe2 me-2"></i>
      Subdominios Registrados
    </h4>

    <div class="mb-3">
      <div class="row g-2 align-items-center">
        <div class="col-md-6">
          <input
            type="text"
            class="form-control"
            placeholder="Buscar por empresa o subdominio..."
            v-model="searchTerm"
            @keyup.enter="loadSubdominios"
          >
        </div>
        <div class="col-md-6 d-flex gap-2 justify-content-md-end">
          <button class="btn btn-primary" @click="loadSubdominios">
            <i class="bi bi-search"></i>
            Buscar
          </button>
          <button class="btn btn-outline-primary" @click="refreshSubdominios">
            <i class="bi bi-arrow-clockwise"></i>
            Actualizar
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-container text-center py-5">
      <div class="d-flex justify-content-center align-items-center gap-3">
        <div class="spinner-border text-primary" role="status"></div>
        <div class="text-start">
          <h6 class="mb-1">Cargando subdominios...</h6>
          <small class="text-muted">Por favor, espere un momento</small>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="error-container">
      <div class="alert alert-danger d-flex align-items-start gap-3">
        <i class="bi bi-exclamation-triangle-fill fs-4"></i>
        <div class="flex-grow-1">
          <h6 class="alert-heading mb-1">Error al cargar subdominios</h6>
          <p class="mb-2">{{ error }}</p>
          <button class="btn btn-sm btn-danger" @click="loadSubdominios">
            <i class="bi bi-arrow-clockwise me-1"></i>
            Reintentar
          </button>
        </div>
      </div>
    </div>

    <div v-else-if="subdominios.length === 0" class="empty-state text-center py-5">
      <div class="mb-4">
        <i class="bi bi-globe2" aria-hidden="true"></i>
      </div>
      <h5 class="mb-2">
        {{ searchTerm ? 'No se encontraron resultados' : 'No hay subdominios registrados' }}
      </h5>
      <p class="mb-3">
        {{ searchTerm
          ? `No se encontraron subdominios que coincidan con "${searchTerm}"`
          : 'Aún no se han registrado subdominios de empresas en el sistema'
        }}
      </p>
      <button v-if="searchTerm" class="btn btn-outline-primary" @click="refreshSubdominios">
        <i class="bi bi-arrow-clockwise me-1"></i>
        Ver todos los subdominios
      </button>
    </div>

    <div v-else class="table-responsive">
      <table class="table table-striped align-middle">
        <thead>
          <tr>
            <th scope="col">Subdominio</th>
            <th scope="col">Empresa</th>
            <th scope="col">Estado</th>
            <th scope="col">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="subdominio in subdominios" :key="subdominio.id">
            <td>
              <strong>{{ getSubdomainUrl(subdominio).replace(/^https?:\/\//, '') }}</strong>
              <br>
              <small>
                <a :href="getSubdomainUrl(subdominio)" target="_blank" rel="noopener">
                  {{ getSubdomainUrl(subdominio) }}
                </a>
              </small>
            </td>
            <td>
              <strong>{{ subdominio.razon_social }}</strong>
              <br>
              <small v-if="subdominio.trade_name">{{ subdominio.trade_name }}</small>
            </td>
            <td>
              <span :class="['status-badge', getStatusBadgeClass(subdominio.status)]">
                {{ getStatusText(subdominio.status) }}
              </span>
            </td>
            <td>
              <a
                :href="getSubdomainUrl(subdominio)"
                target="_blank"
                rel="noopener"
                class="btn btn-sm btn-outline-primary"
              >
                <i class="bi bi-box-arrow-up-right"></i>
                Visitar
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="totalPages > 1" class="d-flex justify-content-center mt-3">
      <nav aria-label="Subdominios paginación">
        <ul class="pagination mb-0">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="previousPage" :disabled="currentPage === 1">
              Anterior
            </button>
          </li>
          <li class="page-item active">
            <span class="page-link">{{ currentPage }} de {{ totalPages }}</span>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="nextPage" :disabled="currentPage === totalPages">
              Siguiente
            </button>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import empresasService from '@/services/empresasService'

export default {
  name: 'EmpresasSubdominios',
  data() {
    return {
      subdominios: [],
      loading: false,
      error: null,
      searchTerm: '',
      currentPage: 1,
      perPage: 10,
      totalSubdominios: 0
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalSubdominios / this.perPage)
    }
  },
  mounted() {
    this.loadSubdominios()
  },
  methods: {
    async loadSubdominios() {
      this.loading = true
      this.error = null

      try {
        const params = new URLSearchParams({
          page: this.currentPage.toString(),
          limit: this.perPage.toString()
        })

        if (this.searchTerm.trim()) {
          params.append('search', this.searchTerm.trim())
        }

        const response = await api.get(`/companies-register/subdominios?${params}`)

        if (response.data.success) {
          this.subdominios = response.data.data
          this.totalSubdominios = response.data.pagination.total
        } else {
          throw new Error('Error en la respuesta del servidor')
        }
      } catch (err) {
        console.error('Error al cargar subdominios:', err)

        if (err.response) {
          const status = err.response.status
          if (status === 404) {
            this.error = 'No se encontró el servicio de subdominios. Contacte al administrador.'
          } else if (status === 401) {
            this.error = 'Su sesión ha expirado. Por favor, inicie sesión nuevamente.'
          } else if (status === 403) {
            this.error = 'No tiene permisos para acceder a esta información.'
          } else if (status === 500) {
            this.error = 'Error interno del servidor. Intente nuevamente en unos momentos.'
          } else {
            this.error = `Error del servidor (${status}). Por favor, intente nuevamente.`
          }
        } else if (err.request) {
          this.error = 'No se pudo conectar al servidor. Verifique su conexión a internet.'
        } else {
          this.error = 'Ocurrió un error inesperado. Por favor, intente nuevamente.'
        }
      } finally {
        this.loading = false
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('es-ES')
    },

    getSubdomainUrl(subdominio) {
      // Check for localhost/development
      if (subdominio.subdomain === 'localhost' || subdominio.id === 0 || subdominio.id === '0') {
        return 'http://localhost:8081'
      }

      const publicDomain = 'sicecom.com'

      // Si el backend ya devolvió un `subdomain` explícito, usarlo (preserva ceros iniciales)
      if (subdominio.subdomain) {
        const sd = String(subdominio.subdomain)
        if (/^https?:\/\//i.test(sd)) return sd
        return `https://${sd}`
      }

      if (subdominio.ruc === '9999999999999' ||
          (subdominio.razon_social && subdominio.razon_social.toLowerCase().includes('demo')) ||
          (subdominio.trade_name && subdominio.trade_name.toLowerCase().includes('demo'))) {
        return `https://demo.${publicDomain}`
      }

      return `https://${subdominio.ruc}.${publicDomain}`
    },

    getStatusText(status) {
      return empresasService.getStatusText(status)
    },

    getStatusBadgeClass(status) {
      const normalizedStatus = empresasService.normalizeStatus
        ? empresasService.normalizeStatus(status)
        : status

      const classMap = {
        active: 'status-active',
        inactive: 'status-inactive',
        suspended: 'status-suspended',
        pending: 'status-pending'
      }

      return classMap[normalizedStatus] || 'status-inactive'
    },

    refreshSubdominios() {
      this.searchTerm = ''
      this.currentPage = 1
      this.loadSubdominios()
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.loadSubdominios()
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.loadSubdominios()
      }
    }
  }
}
</script>

<style>

.table {
  background: var(--secondary-bg-soft);
  color: var(--text-primary);
  border: 1px solid var(--border-main);
  border-radius: 12px;
  overflow: hidden;
}

.table thead th {
  background: var(--secondary-bg-muted);
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 0.875rem;
  border-color: var(--border-main);
}

.table tbody td {
  background: var(--secondary-bg-soft);
  color: var(--text-primary);
  font-size: 0.875rem;
  border-color: var(--border-main);
  vertical-align: middle;
}

.table tbody tr + tr td {
  border-top: 1px solid var(--border-main);
}

.table a {
  color: var(--primary-color);
  text-decoration: none;
}

.table a:hover {
  color: var(--primary-hover);
}

.table-striped tbody tr:nth-of-type(odd) td {
  background: rgba(var(--primary-rgb), 0.08);
}

.table-striped tbody tr:hover td {
  background: rgba(var(--primary-rgb), 0.14);
}

.pagination .page-link {
  font-size: 0.875rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.status-active {
  background: var(--accent-success-light);
  color: var(--accent-success);
}

.status-inactive {
  background: var(--secondary-bg-muted);
  color: var(--text-secondary);
}

.status-suspended,
.status-pending {
  background: var(--accent-warning-light);
  color: var(--accent-warning);
}

.error-container {
  margin: 2rem 0;
}

.error-container .alert {
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(239, 68, 68, 0.18);
  background: rgba(239, 68, 68, 0.12);
  color: var(--text-primary);
}

.error-container .alert-heading {
  font-weight: 600;
  color: #ef4444;
}

.empty-state {
  background: var(--secondary-bg-soft);
  border: 1px solid var(--border-main);
  border-radius: 12px;
  padding: 3rem 2rem;
  margin: 2rem 0;
  color: var(--text-primary);
}

.empty-state .bi-globe2 {
  font-size: 4rem;
  opacity: 0.4;
  color: var(--text-muted);
}

.empty-state h5 {
  font-weight: 600;
}

.empty-state p {
  max-width: 500px;
  margin: 0 auto 1rem;
  color: var(--text-secondary);
}

.loading-container {
  background: var(--secondary-bg-soft);
  border: 1px solid var(--border-main);
  border-radius: 12px;
  margin: 2rem 0;
  color: var(--text-primary);
}

.loading-container h6 {
  font-weight: 600;
}

:deep(.dark-theme) .empty-state .bi-globe2 {
  color: rgba(var(--primary-rgb), 0.55);
}

:deep(.dark-theme) .empty-state h5,
:deep(.dark-theme) .empty-state p {
  color: var(--text-secondary);
}

:deep(.dark-theme) .table-striped tbody tr:nth-of-type(odd) td {
  background: rgba(var(--primary-rgb), 0.14);
}

:deep(.dark-theme) .table-striped tbody tr:hover td {
  background: rgba(var(--primary-rgb), 0.22);
}

:deep(.dark-theme) .table a {
  color: var(--primary-light);
}

:deep(.dark-theme) .table a:hover {
  color: var(--primary-color);
}

:deep(.dark-theme) .status-active {
  background: rgba(16, 185, 129, 0.2);
  color: var(--accent-success);
}

:deep(.dark-theme) .status-inactive {
  background: rgba(148, 163, 184, 0.28);
  color: #e2e8f0;
}

:deep(.dark-theme) .status-suspended,
:deep(.dark-theme) .status-pending {
  background: rgba(245, 158, 11, 0.26);
  color: var(--accent-warning);
}

:deep(.dark-theme) .loading-container {
  border: 1px solid var(--border-main);
}
</style>
